// ══════════════════════════════════════════════════════
//  react/ValuationCard.jsx — 估值倍数 UI
//
//  导出：
//    - <ValuationCard />          独立卡片（数据 tab）
//    - getValuationEvidence()    返回 evidence row 数组（评分 tab）
//
//  两者共用 extract() + judgments，保证一份数据三处一致渲染。
// ══════════════════════════════════════════════════════

import { extract } from '../extractors/valuation';
import { fmtUSD, fmtRatio } from '../extractors/_shared';
import { judgeMcRev, judgeFdvRev } from '../judgments/valuationRatios';

const txt = (lang, zh, en) => (lang === 'en' ? en : zh);

/** 评分 tab 用的 evidence row 数组（push 到现有 evidence[]） */
export function getValuationEvidence(project, a, lang = 'zh') {
  const d = extract(project, a);
  if (!d) return [];
  const rows = [];
  if (d.feesAnn != null) {
    rows.push({
      label: txt(lang, '30日年化手续费', '30d Fees (Annualized)'),
      val: fmtUSD(d.feesAnn),
      source: d.source,
      positive: true,
    });
  }
  if (d.revAnn != null) {
    rows.push({
      label: txt(lang, '30日年化收入', '30d Revenue (Annualized)'),
      val: fmtUSD(d.revAnn),
      source: d.source,
      positive: true,
    });
  }
  if (d.mcRev != null) {
    const j = judgeMcRev(d.mcRev);
    rows.push({
      label: 'MC/Rev (Ann.)',
      val: `${j?.emoji || ''} ${fmtRatio(d.mcRev)}`.trim(),
      source: 'AI',
      positive: j?.positive ?? null,
    });
  }
  if (d.fdvRev != null) {
    const j = judgeFdvRev(d.fdvRev);
    rows.push({
      label: 'FDMC/Rev (Ann.)',
      val: `${j?.emoji || ''} ${fmtRatio(d.fdvRev)}`.trim(),
      source: 'AI',
      positive: j?.positive ?? null,
    });
  }
  return rows;
}

/** 独立卡片：标准 props { project, a, lang, S, C } */
export function ValuationCard({ project, a, lang = 'zh', S, C }) {
  const d = extract(project, a);
  if (!d) return null;

  const ratioRows = [];
  if (d.mcFees != null)  ratioRows.push({ name: 'MC / Fees (Ann.)',   val: fmtRatio(d.mcFees),  judg: null });
  if (d.mcRev != null)   ratioRows.push({ name: 'MC / Revenue (Ann.)', val: fmtRatio(d.mcRev),   judg: judgeMcRev(d.mcRev), accent: true });
  if (d.fdvFees != null) ratioRows.push({ name: 'FDMC / Fees (Ann.)', val: fmtRatio(d.fdvFees), judg: null });
  if (d.fdvRev != null)  ratioRows.push({ name: 'FDMC / Revenue (Ann.)', val: fmtRatio(d.fdvRev), judg: judgeFdvRev(d.fdvRev), accent: true });

  return (
    <div style={S?.card || { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C?.muted || '#6b7280', marginBottom: 10, textTransform: 'uppercase' }}>
        💰 {txt(lang, '估值 & 核心倍数', 'Valuation & Key Multiples')}
        <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 9, color: '#94a3b8' }}>{d.source}</span>
      </div>

      {/* 年化数字（两列）*/}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        {d.feesAnn != null && (
          <div>
            <div style={{ fontSize: 10, color: C?.muted || '#6b7280' }}>{txt(lang, '30日手续费（年化）', '30d Fees (Annualized)')}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{fmtUSD(d.feesAnn)}</div>
          </div>
        )}
        {d.revAnn != null && (
          <div>
            <div style={{ fontSize: 10, color: C?.muted || '#6b7280' }}>{txt(lang, '30日收入（年化）', '30d Revenue (Annualized)')}</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{fmtUSD(d.revAnn)}</div>
          </div>
        )}
      </div>

      {/* 倍数表 */}
      {ratioRows.length > 0 && (
        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', color: C?.muted || '#6b7280' }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>{txt(lang, '估值倍数', 'Ratio')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>{txt(lang, '当前值', 'Value')}</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>{txt(lang, '判断', 'Judgment')}</th>
            </tr>
          </thead>
          <tbody>
            {ratioRows.map((r, i) => (
              <tr key={i} style={{ borderBottom: i < ratioRows.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <td style={{ padding: '6px 8px' }}>{r.name}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: r.accent ? '#2563eb' : '#111' }}>{r.val}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 10 }}>
                  {r.judg ? `${r.judg.emoji} ${txt(lang, r.judg.labelZh, r.judg.labelEn)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
